'use client';
import { useState } from 'react';
import { LoaderCircle, Mic, MicOff, Phone, PhoneOff, Send } from 'lucide-react';
import type { PresentationEvent } from '@/embed/postmessage/protocol';

interface ControlsProps {
  isMuted: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  /** See `PresentationProps.chatMode` — hides the mic toggle, which has no
   * published track behind it in chat mode. */
  chatMode: boolean;
  emit: (e: PresentationEvent) => void;
}

export default function Controls({ isMuted, isConnected, isConnecting, chatMode, emit }: ControlsProps) {
  const [text, setText] = useState('');

  const sendText = () => {
    if (!text.trim()) return;
    emit({ type: 'sendText', text: text.trim() });
    setText('');
  };

  return (
    <div
      className="mobeus-controls"
      part="controls"
      style={{ padding: 8, borderTop: '1px solid var(--mobeus-border, #e5e7eb)', display: 'flex', gap: 6, alignItems: 'center' }}
    >
      {/* Scoped to this component only — hover/active states for the icon
       * buttons below. Colors still resolve through the `--mobeus-*` tokens
       * (with the same hardcoded fallbacks used elsewhere in the file), this
       * just adds the `:hover`/`:active` pseudo-classes inline styles can't
       * express. A later animation pass (styles.css) can build on these
       * class names without touching this file. */}
      <style>{`
        .mobeus-icon-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          padding: 0;
          border: none;
          border-radius: 50%;
          cursor: pointer;
          transition: opacity 0.15s ease, transform 0.1s ease;
        }
        .mobeus-icon-btn:hover:not(:disabled) {
          opacity: 0.85;
        }
        .mobeus-icon-btn:active:not(:disabled) {
          transform: scale(0.95);
        }
        .mobeus-icon-btn:disabled {
          cursor: default;
        }
        .mobeus-connect-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 16px;
          border: none;
          border-radius: 18px;
          cursor: pointer;
          font-weight: 600;
          transition: opacity 0.15s ease, transform 0.1s ease;
        }
        .mobeus-connect-btn:hover {
          opacity: 0.9;
        }
        .mobeus-connect-btn:active {
          transform: scale(0.97);
        }
        @keyframes mobeus-spin { to { transform: rotate(360deg); } }
        .mobeus-spinner { animation: mobeus-spin 0.8s linear infinite; }
      `}</style>
      {!isConnected ? (
        <button
          className={`mobeus-btn mobeus-connect mobeus-connect-btn${isConnecting ? ' mobeus-connecting-pulse' : ''}`}
          part="connect-button"
          onClick={isConnecting ? undefined : () => emit({ type: 'connect' })}
          disabled={isConnecting}
          aria-busy={isConnecting ? 'true' : undefined}
          aria-label={isConnecting ? 'Connecting' : 'Connect'}
          style={{
            background: 'var(--mobeus-primary, #3b82f6)',
            color: 'var(--mobeus-primary-text, #fff)',
            cursor: isConnecting ? 'wait' : 'pointer',
            opacity: isConnecting ? 0.72 : 1,
          }}
        >
          {isConnecting
            ? <LoaderCircle className="mobeus-spinner" size={16} aria-hidden="true" />
            : <Phone size={16} aria-hidden="true" />}
          {isConnecting ? 'Connecting…' : 'Connect'}
        </button>
      ) : (
        <>
          {!chatMode && (
          <button
            className="mobeus-btn mobeus-mute mobeus-icon-btn"
            part="mute-button"
            onClick={() => emit({ type: 'toggleMute' })}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            title={isMuted ? 'Unmute' : 'Mute'}
            style={{
              background: isMuted ? 'var(--mobeus-danger, #ef4444)' : 'var(--mobeus-muted-btn, #6b7280)',
              color: 'var(--mobeus-primary-text, #fff)',
            }}
          >
            {isMuted ? <MicOff size={18} aria-hidden="true" /> : <Mic size={18} aria-hidden="true" />}
          </button>
          )}
          <input
            className="mobeus-input" part="input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // isComposing: an IME (Japanese/Chinese/Korean) commit also
              // fires Enter — that must finish the composition, not send.
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) sendText();
            }}
            placeholder="Type a message"
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 18,
              border: '1px solid var(--mobeus-input-border, #d1d5db)',
              background: 'var(--mobeus-input-bg, #fff)',
              color: 'var(--mobeus-text, #111827)',
            }}
          />
          <button
            className="mobeus-btn mobeus-send mobeus-icon-btn"
            part="send-button"
            onClick={sendText}
            disabled={!text.trim()}
            aria-label="Send message"
            title="Send"
            style={{
              background: 'var(--mobeus-primary, #3b82f6)',
              color: 'var(--mobeus-primary-text, #fff)',
              opacity: text.trim() ? 1 : 0.5,
            }}
          >
            <Send size={16} aria-hidden="true" />
          </button>
          <button
            className="mobeus-btn mobeus-end mobeus-icon-btn"
            part="end-button"
            onClick={() => emit({ type: 'endCall' })}
            aria-label="End call"
            title="End call"
            style={{ background: 'var(--mobeus-danger, #ef4444)', color: 'var(--mobeus-primary-text, #fff)' }}
          >
            <PhoneOff size={18} aria-hidden="true" />
          </button>
        </>
      )}
    </div>
  );
}
